import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Eye, EyeOff, Trash2, Copy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { toast } from "sonner";
import { useTenant } from "@/contexts/TenantContext";
import { generateApiKeyParts } from "@/lib/apiKeys";
import { logCrud } from "@/services/activities";

const tenantSchema = z.object({
  name: z.string().min(3, "Nome deve ter no mínimo 3 caracteres"),
  email: z.string().email("Email inválido").optional(),
});

type TenantFormData = z.infer<typeof tenantSchema>;

export default function CompanySettings() {
  const { tenantId, isAdminSaas, refreshTenant } = useTenant();
  const queryClient = useQueryClient();
  const [generatedTenantKey, setGeneratedTenantKey] = useState<string | null>(null);
  const [showGeneratedKey, setShowGeneratedKey] = useState<boolean>(true);

  // Fetch tenant data
  const { data: tenant } = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: async () => {
      if (!tenantId) return null;
      const { data, error } = await supabase
        .from("tenants" as any)
        .select("*")
        .eq("id", tenantId)
        .single() as any;
      
      if (error) throw error;
      return data;
    },
    enabled: !!tenantId,
  });

  const tenantForm = useForm<TenantFormData>({
    resolver: zodResolver(tenantSchema),
    defaultValues: {
      name: "",
      email: "",
    },
  });

  // Update form when data is loaded
  useEffect(() => {
    if (tenant) {
      tenantForm.reset({
        name: tenant.name || "",
        email: tenant.email || "",
      });
    }
  }, [tenant, tenantForm]);

  const updateTenantMutation = useMutation({
    mutationFn: async (data: TenantFormData) => {
      if (!tenantId) throw new Error("Tenant ID não encontrado");
      
      const { error } = await supabase
        .from("tenants" as any)
        .update({
          name: data.name,
          email: data.email || null,
        })
        .eq("id", tenantId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] });
      refreshTenant();
      toast.success("Dados da empresa atualizados!");
      if (tenantId) {
        void logCrud({ entitySlug: "entity:tenants", operationSlug: "update", entityId: tenantId, screenSlug: "configuracoes/empresa" });
      }
    },
    onError: (error: any) => {
      toast.error("Erro ao atualizar dados: " + error.message);
    },
  });

  return (
    <div className="space-y-6" data-cy="company-settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dados da Empresa</h1>
        <p className="text-muted-foreground">
          Gerencie as informações da sua empresa
        </p>
      </div>

      <Card data-cy="company-card">
        <CardContent className="pt-4">
          {!tenantId || isAdminSaas ? (
            <div className="text-center py-8 text-muted-foreground" data-cy="company-no-data">
              {isAdminSaas 
                ? "Admin SaaS não possui empresa vinculada"
                : "Nenhuma empresa vinculada a este usuário"}
            </div>
          ) : (
            <Form {...tenantForm}>
              <form onSubmit={tenantForm.handleSubmit((data) => updateTenantMutation.mutate(data))} className="space-y-4" data-cy="company-form">
                <FormField
                  control={tenantForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome da Empresa</FormLabel>
                      <FormControl>
                        <Input placeholder="Nome da empresa" {...field} data-cy="company-name-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={tenantForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email da Empresa</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="contato@empresa.com" {...field} data-cy="company-email-input" />
                      </FormControl>
                      <FormDescription>
                        Email principal para contato
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" disabled={updateTenantMutation.isPending} className="w-full" data-cy="company-save-btn">
                  {updateTenantMutation.isPending ? "Salvando..." : "Salvar Alterações"}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>

      {/* API Keys do Tenant */}
      <Card data-cy="tenant-apikey-card">
        <CardHeader>
          <CardTitle>API Keys do Tenant</CardTitle>
          <CardDescription>Gere e gerencie chaves para integrações externas</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!tenantId || isAdminSaas ? (
            <div className="text-center py-8 text-muted-foreground" data-cy="tenant-apikey-no-data">
              {isAdminSaas
                ? "Admin SaaS não possui empresa vinculada"
                : "Nenhuma empresa vinculada a este usuário"}
            </div>
          ) : (
            <TenantApiKeysSection tenantId={tenantId} onGenerated={(full) => setGeneratedTenantKey(full)} />
          )}

          {false && generatedTenantKey && (
            <div />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TenantApiKeysSection({ tenantId, onGenerated }: { tenantId: string; onGenerated?: (full: string) => void }) {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [showMap, setShowMap] = useState<Record<string, boolean>>({});
  const { data: keys } = useQuery({
    queryKey: ["api_keys", tenantId, "tenant"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("api_keys" as any)
        .select("id, name, enabled, key_prefix, key_full, created_at, last_used_at, expires_at")
        .eq("tenant_id", tenantId)
        .eq("type", "tenant")
        .order("created_at", { ascending: false }) as any;
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId,
  });

  const createKeyMutation = useMutation({
    mutationFn: async () => {
      const { prefix, full, hash } = await generateApiKeyParts("tenant");
      const { error } = await supabase
        .from("api_keys" as any)
        .insert({
          tenant_id: tenantId,
          type: "tenant",
          key_hash: hash,
          key_prefix: prefix,
          key_full: full,
          name: "Chave do Tenant",
          enabled: true,
          scopes: { "*": ["*"] },
        });
      if (error) throw error;
      onGenerated && onGenerated(full);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api_keys", tenantId, "tenant"] });
      toast.success("API Key gerada!");
      void logCrud({ entitySlug: "entity:api_keys", operationSlug: "create", screenSlug: "configuracoes/empresa" });
    },
    onError: (error: any) => {
      toast.error("Erro ao gerar chave: " + error.message);
    },
  });

  const toggleEnabledMutation = useMutation({
    mutationFn: async (payload: { id: string; enabled: boolean }) => {
      const { error } = await supabase
        .from("api_keys" as any)
        .update({ enabled: payload.enabled })
        .eq("id", payload.id);
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["api_keys", tenantId, "tenant"] });
      toast.success(variables.enabled ? "Chave ativada!" : "Chave desativada!");
      void logCrud({ entitySlug: "entity:api_keys", operationSlug: "update", screenSlug: "configuracoes/empresa" });
    },
    onError: (error: any) => {
      toast.error("Erro ao atualizar chave: " + error.message);
    },
  });

  const deleteKeyMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("api_keys" as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api_keys", tenantId, "tenant"] });
      toast.success("API Key excluída");
      void logCrud({ entitySlug: "entity:api_keys", operationSlug: "delete", screenSlug: "configuracoes/empresa" });
    },
    onError: (error: any) => {
      toast.error("Erro ao excluir chave: " + error.message);
    },
  });

  return (
    <div className="space-y-3" data-cy="tenant-apikey-section">
      <Button onClick={() => createKeyMutation.mutate()} disabled={createKeyMutation.isPending} data-cy="tenant-apikey-generate-btn">
        {createKeyMutation.isPending ? "Gerando..." : "Gerar Nova API Key"}
      </Button>

      <div className="space-y-2">
        {keys?.length ? (
          keys.map((k: any) => (
            <div key={k.id} className="flex items-center justify-between rounded-md border p-3" data-cy="tenant-apikey-item">
              <div>
                <p className="text-sm font-medium">{k.name || "Chave"}</p>
                <p className="text-xs text-muted-foreground">Criada: {new Date(k.created_at).toLocaleString()}</p>
                {k.last_used_at && (
                  <p className="text-xs text-muted-foreground">Último uso: {new Date(k.last_used_at).toLocaleString()}</p>
                )}
                <div className="mt-2">
                  <code className="text-xs font-mono break-all">{showMap[k.id] ? k.key_full : "••••••••••"}</code>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => setShowMap((m) => ({ ...m, [k.id]: !m[k.id] }))}>
                  {showMap[k.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    navigator.clipboard.writeText(k.key_full);
                    toast.success("Chave copiada!");
                  }}
                  title="Copiar"
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleEnabledMutation.mutate({ id: k.id, enabled: !k.enabled })}
                >{k.enabled ? "Desativar" : "Ativar"}</Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => { setPendingDeleteId(k.id); setConfirmOpen(true); }}
                  title="Excluir"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-sm text-muted-foreground" data-cy="tenant-apikey-empty">Nenhuma chave criada ainda</div>
        )}
      </div>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir API Key</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancelar</Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (pendingDeleteId) deleteKeyMutation.mutate(pendingDeleteId);
                  setConfirmOpen(false);
                  setPendingDeleteId(null);
                }}
              >Excluir</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

